import React, { useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Divider } from 'primereact/divider';
import { 
  validateCardNumber, 
  validateExpiryDate, 
  validateCVV, 
  validateCardholderName,
  formatCardNumber,
  detectCardType 
} from '../../utils/cardValidation';

/**
 * Payment Form Component
 * Secure card details input form with real-time validation
 */
const PaymentForm = ({ onSubmit, onCancel, isLoading = false, total = 0 }) => {
  const [cardDetails, setCardDetails] = useState({
    cardholderName: '',
    cardType: '', // User-selected card type
    cardNumber: '',
    cardExpiry: '', // Single field: MM/YY
    cvv: ''
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [cardType, setCardType] = useState(null);
  const expiryInputRef = React.useRef(null);

  // Opayo-compatible card types (UK focus)
  const cardTypes = [
    { label: 'Visa', value: 'VISA' },
    { label: 'Mastercard', value: 'MC' },
    { label: 'Maestro', value: 'MCDEBIT' },
    { label: 'American Express', value: 'AMEX' },
    { label: 'Visa Debit', value: 'DELTA' },
    { label: 'Visa Electron', value: 'UKE' }
  ];

  // Parse expiry into month and year
  const parseExpiry = (expiry) => {
    const cleaned = expiry.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return {
        month: cleaned.substring(0, 2),
        year: cleaned.substring(2, 4)
      };
    }
    return { month: '', year: '' };
  };

  // Validate field on blur
  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, cardDetails[field]);
  };

  // Validate individual field
  const validateField = (field, value) => {
    let validation = { isValid: true };

    switch (field) {
      case 'cardholderName':
        validation = validateCardholderName(value);
        break;
      case 'cardNumber':
        validation = validateCardNumber(value);
        if (validation.isValid && validation.cardType) {
          setCardType(validation.cardType);
        }
        break;
      case 'cardExpiry':
        const { month, year } = parseExpiry(value);
        validation = validateExpiryDate(month, year);
        break;
      case 'cvv':
        validation = validateCVV(value, cardType);
        break;
      default:
        break;
    }

    setErrors(prev => ({
      ...prev,
      [field]: validation.isValid ? null : validation.error
    }));

    return validation.isValid;
  };

  // Handle input change
  const handleChange = (field, value, event) => {
    let processedValue = value;
    let newCursorPosition = null;

    // Special handling for card number
    if (field === 'cardNumber') {
      // Remove non-digits
      const digits = value.replace(/\D/g, '');
      // Limit to 19 digits
      processedValue = digits.slice(0, 19);
      
      // Detect card type
      const type = detectCardType(processedValue);
      setCardType(type);
    }

    // Special handling for card expiry (MM/YY format)
    if (field === 'cardExpiry' && event) {
      const input = event.target;
      const cursorPosition = input.selectionStart;
      const oldValue = cardDetails.cardExpiry;
      
      // Remove all non-digits and spaces
      const digits = value.replace(/\D/g, '');
      
      // Limit to 4 digits (MMYY)
      const limited = digits.slice(0, 4);
      
      // Format as MM/YY
      if (limited.length >= 3) {
        // Has at least MMY, add slash
        processedValue = limited.slice(0, 2) + '/' + limited.slice(2);
      } else if (limited.length === 2) {
        // Check if we should auto-add slash (when user types second digit)
        const month = parseInt(limited, 10);
        if (month > 12) {
          // Invalid month, limit to first digit
          processedValue = limited.slice(0, 1);
        } else {
          // Valid month, just show the digits (slash will be added on next input)
          processedValue = limited;
        }
      } else {
        processedValue = limited;
      }

      // Calculate new cursor position
      const oldDigits = oldValue.replace(/\D/g, '');
      const newDigits = processedValue.replace(/\D/g, '');
      
      if (oldValue.length > processedValue.length) {
        // Deletion occurred
        if (cursorPosition === 3 && oldValue[2] === '/') {
          // User deleted the slash or character after it, move cursor before slash position
          newCursorPosition = 2;
        } else if (cursorPosition <= 2) {
          // User deleted in the month section
          newCursorPosition = cursorPosition;
        } else {
          // User deleted in year section
          newCursorPosition = cursorPosition;
        }
      } else if (newDigits.length === 2 && oldDigits.length === 1 && cursorPosition === 2) {
        // User just typed the second digit of month, keep cursor at position 2
        // (The slash will appear but cursor stays before it)
        newCursorPosition = 2;
      } else if (newDigits.length === 3 && oldDigits.length === 2 && processedValue.includes('/')) {
        // User typed first digit of year after MM/, cursor should be at position 4 (after MM/Y)
        newCursorPosition = 4;
      } else if (processedValue.includes('/') && !oldValue.includes('/') && cursorPosition > 2) {
        // Slash was just added and cursor was after where slash appeared, adjust cursor
        newCursorPosition = cursorPosition + 1;
      } else if (cursorPosition <= 2) {
        // Typing in month section
        newCursorPosition = Math.min(cursorPosition, processedValue.length);
      } else {
        // Default: maintain relative position
        newCursorPosition = Math.min(cursorPosition, processedValue.length);
      }
    }

    // Special handling for CVV
    if (field === 'cvv') {
      const digits = value.replace(/\D/g, '');
      const maxLength = cardType === 'amex' ? 4 : 3;
      processedValue = digits.slice(0, maxLength);
    }

    setCardDetails(prev => ({ ...prev, [field]: processedValue }));

    // Set cursor position after React updates the input
    if (field === 'cardExpiry' && newCursorPosition !== null && expiryInputRef.current) {
      setTimeout(() => {
        if (expiryInputRef.current) {
          expiryInputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
        }
      }, 0);
    }

    // Clear error when user starts typing
    if (touched[field] && errors[field]) {
      validateField(field, processedValue);
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    // Parse expiry
    const { month, year } = parseExpiry(cardDetails.cardExpiry);

    // Mark all fields as touched
    setTouched({
      cardholderName: true,
      cardType: true,
      cardNumber: true,
      cardExpiry: true,
      cvv: true
    });

    // Validate all fields
    const validations = {
      cardholderName: validateCardholderName(cardDetails.cardholderName),
      cardType: cardDetails.cardType ? { isValid: true } : { isValid: false, error: 'Please select card type' },
      cardNumber: validateCardNumber(cardDetails.cardNumber),
      expiry: validateExpiryDate(month, year),
      cvv: validateCVV(cardDetails.cvv, cardType)
    };

    const newErrors = {};
    if (!validations.cardholderName.isValid) {
      newErrors.cardholderName = validations.cardholderName.error;
    }
    if (!validations.cardType.isValid) {
      newErrors.cardType = validations.cardType.error;
    }
    if (!validations.cardNumber.isValid) {
      newErrors.cardNumber = validations.cardNumber.error;
    }
    if (!validations.expiry.isValid) {
      newErrors.cardExpiry = validations.expiry.error;
    }
    if (!validations.cvv.isValid) {
      newErrors.cvv = validations.cvv.error;
    }

    setErrors(newErrors);

    // If validation passes, submit with parsed month/year
    if (Object.keys(newErrors).length === 0) {
      onSubmit({
        ...cardDetails,
        expiryMonth: month,
        expiryYear: year
      });
    }
  };

  // Get card type icon
  const getCardIcon = () => {
    const iconMap = {
      visa: 'pi-credit-card',
      mastercard: 'pi-credit-card',
      amex: 'pi-credit-card',
      discover: 'pi-credit-card',
      dinersclub: 'pi-credit-card',
      jcb: 'pi-credit-card'
    };

    return iconMap[cardType] || 'pi-credit-card';
  };

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      <div className="mb-4">
        <Message
          severity="info"
          text={`Total Amount: £${total.toFixed(2)}`}
          className="w-full"
        />
      </div>

      {/* Cardholder Name */}
      <div className="se-form-group">
        <label htmlFor="cardholderName" className="se-form-label">
          Cardholder Name <span className="se-form-required">*</span>
        </label>
        <InputText
          id="cardholderName"
          value={cardDetails.cardholderName}
          onChange={(e) => handleChange('cardholderName', e.target.value)}
          onBlur={() => handleBlur('cardholderName')}
          placeholder="Name as shown on card"
          className={`w-full ${errors.cardholderName && touched.cardholderName ? 'p-invalid' : ''}`}
          disabled={isLoading}
          autoComplete="cc-name"
        />
        {errors.cardholderName && touched.cardholderName && (
          <small className="p-error">{errors.cardholderName}</small>
        )}
      </div>

      {/* Card Type */}
      <div className="se-form-group">
        <label htmlFor="cardType" className="se-form-label">
          Card Type <span className="se-form-required">*</span>
        </label>
        <Dropdown
          id="cardType"
          value={cardDetails.cardType}
          options={cardTypes}
          onChange={(e) => handleChange('cardType', e.value)}
          onBlur={() => handleBlur('cardType')}
          placeholder="Select card type"
          className={`w-full ${errors.cardType && touched.cardType ? 'p-invalid' : ''}`}
          disabled={isLoading}
        />
        {errors.cardType && touched.cardType && (
          <small className="p-error">{errors.cardType}</small>
        )}
      </div>

      {/* Card Number */}
      <div className="se-form-group">
        <label htmlFor="cardNumber" className="se-form-label">
          Card Number <span className="se-form-required">*</span>
        </label>
        <div className="p-inputgroup">
          <InputText
            id="cardNumber"
            value={formatCardNumber(cardDetails.cardNumber)}
            onChange={(e) => handleChange('cardNumber', e.target.value)}
            onBlur={() => handleBlur('cardNumber')}
            placeholder="1234 5678 9012 3456"
            className={`w-full ${errors.cardNumber && touched.cardNumber ? 'p-invalid' : ''}`}
            disabled={isLoading}
            autoComplete="cc-number"
            maxLength={23}
          />
          <span className="p-inputgroup-addon">
            <i className={`pi ${getCardIcon()}`}></i>
          </span>
        </div>
        {errors.cardNumber && touched.cardNumber && (
          <small className="p-error">{errors.cardNumber}</small>
        )}
        {cardType && !errors.cardNumber && (
          <small className="text-600 capitalize">{cardType}</small>
        )}
      </div>

      {/* Expiry Date and CVV */}
      <div className="grid">
        <div className="col-12 md:col-6">
          <div className="se-form-group">
            <label htmlFor="cardExpiry" className="se-form-label">
              Expiry Date <span className="se-form-required">*</span>
            </label>
            <InputText
              ref={expiryInputRef}
              id="cardExpiry"
              value={cardDetails.cardExpiry}
              onChange={(e) => handleChange('cardExpiry', e.target.value, e)}
              onBlur={() => handleBlur('cardExpiry')}
              placeholder="MM/YY"
              className={`w-full ${errors.cardExpiry && touched.cardExpiry ? 'p-invalid' : ''}`}
              disabled={isLoading}
              autoComplete="cc-exp"
              maxLength={5}
            />
            {errors.cardExpiry && touched.cardExpiry && (
              <small className="p-error">{errors.cardExpiry}</small>
            )}
          </div>
        </div>

        <div className="col-12 md:col-6">
          <div className="se-form-group">
            <label htmlFor="cvv" className="se-form-label">
              Security Code <span className="se-form-required">*</span>
            </label>
            <InputText
              id="cvv"
              value={cardDetails.cvv}
              onChange={(e) => handleChange('cvv', e.target.value)}
              onBlur={() => handleBlur('cvv')}
              placeholder={cardType === 'amex' ? '1234' : '123'}
              className={`w-full ${errors.cvv && touched.cvv ? 'p-invalid' : ''}`}
              disabled={isLoading}
              autoComplete="cc-csc"
              maxLength={cardType === 'amex' ? 4 : 3}
              type="password"
            />
            {errors.cvv && touched.cvv && (
              <small className="p-error">{errors.cvv}</small>
            )}
            {!errors.cvv && (
              <small className="text-600">
                {cardType === 'amex' ? '4 digits on front' : '3 digits on back'}
              </small>
            )}
          </div>
        </div>
      </div>

      <Divider />

      {/* Security Notice */}
      <div className="mb-4">
        <Message
          severity="success"
          text="🔒 Your payment is secure and encrypted. Card details are processed by Opayo (SagePay)."
          className="w-full"
        />
      </div>

      {/* Action Buttons */}
      <div className="flex justify-content-end gap-2">
        <Button
          type="button"
          label="Cancel"
          icon="pi pi-times"
          className="p-button-text"
          onClick={onCancel}
          disabled={isLoading}
        />
        <Button
          type="submit"
          label={`Pay £${total.toFixed(2)}`}
          icon="pi pi-lock"
          className="p-button-primary"
          loading={isLoading}
        />
      </div>
    </form>
  );
};

export default PaymentForm;
